import { c as _c } from "react/compiler-runtime";
import { DecisionMetadata, ReferralWorkflowTimeline, type ReferralWorkflowStatus, type ReferralWorkflowStep } from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import { VerificationButtons } from "@/src/components/tasks/verification-buttons";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
function stepStatus(step: TaskStep | undefined, confirmed: boolean, held: boolean): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (held || step.status === TaskStepStatus.PAUSED) return "needs_action";
  if (step.status === TaskStepStatus.ASYNC) return "waiting";
  // Early exits are also persisted as completed. Require success evidence before showing the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return "automated";
  return "needs_action";
}
function outputId(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}
export function ConfluentReferralSteps(t0) {
  const $ = _c(65);
  const {
    task,
    taskSteps,
    pendingApproval
  } = t0;
  let onHold;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== task.status || $[1] !== taskSteps) {
    const patientStep = taskSteps.find(_temp);
    const caseStep = taskSteps.find(_temp2);
    const configureStep = taskSteps.find(_temp3);
    const patientId = outputId(patientStep, "patientEhrId");
    const caseId = outputId(caseStep, "patientCaseEhrId");
    let t6;
    if ($[8] !== taskSteps) {
      t6 = taskSteps.at(-1);
      $[8] = taskSteps;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const lastStep = t6;
    onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
    let t7;
    if ($[10] !== lastStep?.id || $[11] !== onHold) {
      t7 = step_2 => onHold && step_2 !== undefined && step_2.id === lastStep?.id;
      $[10] = lastStep?.id;
      $[11] = onHold;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const isHeld = t7;
    const t8 = stepStatus(patientStep, patientId !== null, isHeld(patientStep));
    const t9 = patientId ?? "Not recorded";
    let t10;
    if ($[13] !== t9) {
      t10 = <DecisionMetadata items={[{
        label: "Clinicient patient ID",
        value: t9
      }]} />;
      $[13] = t9;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    if ($[15] !== t10 || $[16] !== t8) {
      t4 = {
        number: 1,
        title: "Ensure the patient chart exists",
        status: t8,
        summary: "Find the linked patient or create a new chart in Clinicient. Existing chart demographics are not updated.",
        content: t10
      };
      $[15] = t10;
      $[16] = t8;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    const t11 = stepStatus(caseStep, caseId !== null, isHeld(caseStep));
    const t12 = caseId ?? "Not recorded";
    let t13;
    if ($[18] !== t12) {
      t13 = <DecisionMetadata items={[{
        label: "Clinicient case ID",
        value: t12
      }]} />;
      $[18] = t12;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    if ($[20] !== t11 || $[21] !== t13) {
      t5 = {
        number: 2,
        title: "Create the referral case",
        status: t11,
        summary: "Create the referral case after checking for an existing case that needs review.",
        content: t13
      };
      $[20] = t11;
      $[21] = t13;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    t1 = 3;
    t2 = "Configure the case";
    t3 = stepStatus(configureStep, caseId !== null, isHeld(configureStep));
    $[0] = task.status;
    $[1] = taskSteps;
    $[2] = onHold;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    onHold = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-sm">Verify the case details in Clinicient before completing the task.</p>;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t3) {
    t7 = {
      number: t1,
      title: t2,
      status: t3,
      summary: "Apply the case description, onset date when available, and configured payment and workers\u2019 compensation fields.",
      content: t6
    };
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let hasAsyncStep;
  let isCompleted;
  let isProcessing;
  let needsAttention;
  let note;
  let steps;
  if ($[28] !== onHold || $[29] !== pendingApproval || $[30] !== t4 || $[31] !== t5 || $[32] !== t7 || $[33] !== task || $[34] !== taskSteps) {
    steps = [t4, t5, t7];
    const allSynced = steps.every(_temp4);
    isCompleted = task.status === TaskStatus.COMPLETED;
    isProcessing = task.status === TaskStatus.PROCESSING;
    let t8;
    if ($[41] !== taskSteps) {
      t8 = taskSteps.some(_temp5);
      $[41] = taskSteps;
      $[42] = t8;
    } else {
      t8 = $[42];
    }
    const hasFailedStep = t8;
    let t9;
    if ($[43] !== taskSteps) {
      t9 = taskSteps.some(_temp6);
      $[43] = taskSteps;
      $[44] = t9;
    } else {
      t9 = $[44];
    }
    const hasPausedStep = t9;
    let t10;
    if ($[45] !== taskSteps) {
      t10 = taskSteps.some(_temp7);
      $[45] = taskSteps;
      $[46] = t10;
    } else {
      t10 = $[46];
    }
    hasAsyncStep = t10;
    const workflowNeedsAttention = onHold || hasFailedStep || hasPausedStep;
    const canVerify = task.status === TaskStatus.VERIFICATION_NEEDED && allSynced && !pendingApproval && !workflowNeedsAttention && !hasAsyncStep;
    let t11;
    if ($[47] !== task.files) {
      t11 = task.files.find(_temp8)?.note;
      $[47] = task.files;
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    note = t11;
    needsAttention = workflowNeedsAttention || Boolean(pendingApproval) || steps.some(_temp9);
    const t12 = isCompleted ? "verified" : hasFailedStep ? "failed" : workflowNeedsAttention || pendingApproval || canVerify ? "needs_action" : "waiting";
    const t13 = isCompleted ? "This task has been marked complete." : pendingApproval ? "Complete the pending workflow confirmation before verifying this task." : workflowNeedsAttention ? "Resolve the outstanding workflow issue before verifying this task." : isProcessing || hasAsyncStep ? "The workflow is continuing. Verify the results when it finishes." : !allSynced ? "Resolve the incomplete sync steps above before verifying this task." : "Confirm the patient and case details in Clinicient and complete the remaining manual work.";
    let t14;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <p className="text-sm">Verify the insurance and referral documents in Clinicient. Complete any remaining secondary insurance and authorization work before completing this task.</p>;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    let t15;
    if ($[50] !== canVerify || $[51] !== task) {
      t15 = canVerify ? {
        instruction: "After verifying the results and completing the manual work in Clinicient, continue to complete this task.",
        control: <VerificationButtons task={task} continueOnly={true} />
      } : undefined;
      $[50] = canVerify;
      $[51] = task;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    let t16;
    if ($[53] !== t12 || $[54] !== t13 || $[55] !== t15) {
      t16 = {
        number: 4,
        title: "Verify in Clinicient",
        status: t12,
        summary: t13,
        content: t14,
        continuation: t15
      };
      $[53] = t12;
      $[54] = t13;
      $[55] = t15;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    steps.push(t16);
    $[28] = onHold;
    $[29] = pendingApproval;
    $[30] = t4;
    $[31] = t5;
    $[32] = t7;
    $[33] = task;
    $[34] = taskSteps;
    $[35] = hasAsyncStep;
    $[36] = isCompleted;
    $[37] = isProcessing;
    $[38] = needsAttention;
    $[39] = note;
    $[40] = steps;
  } else {
    hasAsyncStep = $[35];
    isCompleted = $[36];
    isProcessing = $[37];
    needsAttention = $[38];
    note = $[39];
    steps = $[40];
  }
  const t8 = isProcessing || hasAsyncStep;
  const t9 = !isCompleted && taskSteps.length === 0 && !needsAttention ? "No Clinicient sync steps have been recorded for this task yet." : undefined;
  let t10;
  if ($[57] !== needsAttention || $[58] !== note) {
    t10 = needsAttention && note ? <p className="whitespace-pre-wrap text-sm">{note}</p> : undefined;
    $[57] = needsAttention;
    $[58] = note;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  let t11;
  if ($[60] !== steps || $[61] !== t10 || $[62] !== t8 || $[63] !== t9) {
    t11 = <ReferralWorkflowTimeline steps={steps} isProcessing={t8} waitingMessage={t9} summaryContent={t10} />;
    $[60] = steps;
    $[61] = t10;
    $[62] = t8;
    $[63] = t9;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  return t11;
}
function _temp9(step_7) {
  return step_7.status === "needs_action" || step_7.status === "failed";
}
function _temp8(file) {
  return file.type === FileType.INBOUND;
}
function _temp7(step_6) {
  return step_6.status === TaskStepStatus.ASYNC;
}
function _temp6(step_5) {
  return step_5.status === TaskStepStatus.PAUSED;
}
function _temp5(step_4) {
  return step_4.status === TaskStepStatus.FAILED;
}
function _temp4(step_3) {
  return step_3.status === "automated";
}
function _temp3(step_1) {
  return step_1.stepKey === StepKey.CLINICIENT_CONFIGURE_CASE;
}
function _temp2(step_0) {
  return step_0.stepKey === StepKey.CLINICIENT_ENSURE_CASE;
}
function _temp(step) {
  return step.stepKey === StepKey.ENSURE_PATIENT;
}